// Touchpoint Health section — dashboard parity v3 + v3.8.
//
// Mirrors v1's "Touchpoint Health" surface in the Account
// Analytics group: a paginated chart of Accounts ranked by days
// since their last Touchpoint, with an engagement badge
// (Active / Cooling / Cold / Never).
//
// v3.8 flips the chart from horizontal rows to vertical columns —
// each Account becomes a tall bar (height ≈ days since last
// Touchpoint, capped at 100 days), with the day count above and
// the Account name + status badge below. Same drill-through:
// click the column to open the Account.
//
// Reads from the new ``/api/v1/dashboard/touchpoint_health``
// endpoint, which is broker-scoped and returns up to 50 rows by
// default.

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  DashboardCard,
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
  useTouchpointHealthQuery,
  type TouchpointHealthRow,
  type TouchpointHealthStatus,
} from "@/features/dashboard";


export const TOUCHPOINT_HEALTH_SECTION = "touchpoint-health" as const;

// Mirror v1's per-page pagination. Backend caps at 50 rows total
// so 6 per page is comfortable.
const PAGE_SIZE = 6;
const COLUMN_HEIGHT = 160; // px — matches the Employee Analytics columns
const COLD_DAYS_CAP = 100;


const RECENCY_OPTIONS: Array<{
  value: TouchpointHealthStatus; label: string; chip: string;
}> = [
  { value: "active", label: "Active", chip: "bg-sage-tint text-teal" },
  { value: "cooling", label: "Cooling", chip: "bg-gold-tint text-gold" },
  { value: "cold", label: "Cold", chip: "bg-ember-tint text-ember" },
  { value: "never", label: "Never", chip: "bg-recessed text-text-secondary" },
];


export function TouchpointHealthSection() {
  const [page, setPage] = useState(0);
  const [activeRecency, setActiveRecency] =
    useState<TouchpointHealthStatus[]>([]);
  const query = useTouchpointHealthQuery({ limit: 60 });

  const rows = useMemo(() => {
    const all = query.data?.rows ?? [];
    if (activeRecency.length === 0) return all;
    const set = new Set(activeRecency);
    return all.filter((r) => set.has(r.status));
  }, [query.data, activeRecency]);
  const total =
    activeRecency.length === 0
      ? query.data?.total ?? 0
      : rows.length;
  const start = page * PAGE_SIZE;
  const visible = rows.slice(start, start + PAGE_SIZE);
  const hasPrev = start > 0;
  const hasNext = start + visible.length < rows.length;

  function toggleRecency(s: TouchpointHealthStatus): void {
    setActiveRecency((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
    setPage(0);
  }

  return (
    <DashboardCard
      section={TOUCHPOINT_HEALTH_SECTION}
      title="Touchpoint Health"
      subtitle="Days since last Touchpoint by Account. Click an Account to open it."
      actions={
        <RecencyChips value={activeRecency} onToggle={toggleRecency} />
      }
    >
      {query.isLoading ? (
        <DashboardLoadingState variant="list" rows={5} />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load Touchpoint Health"
          retry={() => query.refetch()}
        />
      ) : visible.length === 0 ? (
        <DashboardEmptyState
          title="No Accounts to surface"
          description="Once you log Touchpoints against your Accounts, this list fills in with the coldest first."
        />
      ) : (
        <>
          <ul
            role="list"
            data-testid="touchpoint-health-bars"
            className="flex items-end justify-around gap-3 rounded-md bg-canvas/40 px-3 pt-3 pb-2"
            style={{ minHeight: COLUMN_HEIGHT + 80 }}
          >
            {visible.map((row) => (
              <Column key={row.account_id} row={row} />
            ))}
          </ul>
          <footer className="mt-3 flex items-center justify-between text-[11px] text-text-tertiary">
            <span>
              Showing {start + 1}–{start + visible.length} of {total}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                data-testid="touchpoint-health-prev"
                aria-label="Previous Accounts"
                disabled={!hasPrev}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="inline-flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:bg-recessed disabled:cursor-not-allowed disabled:text-text-tertiary"
              >
                <ChevronLeft size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                data-testid="touchpoint-health-next"
                aria-label="Next Accounts"
                disabled={!hasNext}
                onClick={() => setPage((p) => p + 1)}
                className="inline-flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:bg-recessed disabled:cursor-not-allowed disabled:text-text-tertiary"
              >
                <ChevronRight size={14} aria-hidden="true" />
              </button>
            </div>
          </footer>
        </>
      )}
    </DashboardCard>
  );
}


function Column({ row }: { row: TouchpointHealthRow }) {
  // Cap the bar height at 100 days so the deepest cold doesn't
  // tower over the page; ``never`` renders a fully-filled ember
  // column.
  const heightPct =
    row.status === "never"
      ? 100
      : Math.min(
          100,
          Math.max(2, ((row.days_since_touchpoint ?? 0) / COLD_DAYS_CAP) * 100),
        );
  const dayLabel =
    row.status === "never"
      ? "Never"
      : `${row.days_since_touchpoint ?? 0}d`;
  return (
    <li
      key={row.account_id}
      className="flex min-w-0 flex-1 flex-col items-center gap-1"
    >
      <Link
        to={`/dashboard/accounts/${row.account_id}`}
        data-testid={`touchpoint-health-row-${row.account_id}`}
        title={row.legal_name}
        aria-label={
          row.status === "never"
            ? `${row.legal_name}: no Touchpoints recorded`
            : `${row.legal_name}: ${row.days_since_touchpoint ?? 0} days since last Touchpoint`
        }
        className="flex w-full flex-col items-center gap-1 rounded-md px-1 py-1 hover:bg-raised"
      >
        {/* Day count above the column */}
        <span className="text-xs font-medium text-text-primary tabular-nums">
          {dayLabel}
        </span>
        {/* The column itself */}
        <span
          className="relative flex w-full items-end justify-center overflow-hidden rounded-t-md bg-recessed/40"
          style={{ height: COLUMN_HEIGHT }}
        >
          <span
            className={["block w-full rounded-t-md", barFill(row.status)].join(" ")}
            style={{ height: `${heightPct}%` }}
            aria-hidden="true"
          />
        </span>
        {/* Account name below */}
        <span
          className="w-full truncate text-center text-[11px] font-medium text-text-primary"
        >
          {row.legal_name}
        </span>
        {/* Status badge */}
        <span
          data-testid={`touchpoint-health-status-${row.account_id}`}
          className={[
            "mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-semibold",
            badgeFill(row.status),
          ].join(" ")}
        >
          {labelFor(row.status, row.days_since_touchpoint)}
        </span>
      </Link>
    </li>
  );
}


function barFill(status: TouchpointHealthStatus): string {
  switch (status) {
    case "cold": return "bg-ember";
    case "cooling": return "bg-gold";
    case "active": return "bg-sage";
    case "never": return "bg-ember/60";
    default: return "bg-recessed";
  }
}


function badgeFill(status: TouchpointHealthStatus): string {
  switch (status) {
    case "cold": return "bg-ember-tint text-ember";
    case "cooling": return "bg-gold-tint text-gold";
    case "active": return "bg-sage-tint text-teal";
    case "never": return "bg-recessed text-text-tertiary";
    default: return "bg-recessed text-text-tertiary";
  }
}


function labelFor(
  status: TouchpointHealthStatus,
  days: number | null,
): string {
  if (status === "never") return "Never";
  if (days == null) return "—";
  return `${days}d`;
}


function RecencyChips({
  value, onToggle,
}: {
  value: TouchpointHealthStatus[];
  onToggle: (s: TouchpointHealthStatus) => void;
}) {
  const set = new Set(value);
  return (
    <div
      data-testid="touchpoint-recency-chips"
      data-count={value.length}
      role="group"
      aria-label="Touchpoint recency filter"
      className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-raised p-1"
    >
      {RECENCY_OPTIONS.map((opt) => {
        const active = set.has(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            data-testid={`touchpoint-recency-${opt.value}`}
            data-active={active ? "true" : "false"}
            onClick={() => onToggle(opt.value)}
            className={[
              "rounded px-1.5 py-0.5 text-[11px] font-medium transition-fast ease-out",
              active
                ? opt.chip
                : "text-text-secondary hover:text-text-primary",
            ].join(" ")}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
